import { ReactElement } from "react";
import {
  Show,
  ShowDealType,
  TicketType,
} from "@quantizr-engineering/common/types/show";
import {
  calculateNetAdjustments,
  calculateNetDeductions,
  calculateSellableCap,
  calculateShowFigures,
  calculateTicketsSoldActual,
  calculateTicketsSoldProjected,
} from "@quantizr-engineering/common/math/live";
import { isShowInActualStatus } from "../../utils/showStatus";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from "../../utils/numberFormatters";
import { PiSparkle } from "react-icons/pi";
import { FaTicketAlt } from "react-icons/fa";
import Tooltip from "../Tooltip";
import { stringLengthLimiter } from "../../utils/utils";

const projectedTicketBonusInsight = (show: Show): ReactElement => {
  let numberRemaining = 0;
  const ticketsSoldProjected = calculateShowFigures(show).ticketsSoldProjected;
  const soldPercent =
    (calculateTicketsSoldProjected(show) / calculateSellableCap(show)) * 100;

  for (const bonus of show.ticketBonuses) {
    if (bonus.threshold && bonus.threshold <= ticketsSoldProjected) {
      continue;
    } else if (
      bonus.thresholdPercent &&
      bonus.thresholdPercent <= soldPercent
    ) {
      continue;
    } else {
      numberRemaining = !!bonus.threshold
        ? bonus.threshold - ticketsSoldProjected
        : Math.ceil(
            ((bonus.thresholdPercent - soldPercent) *
              calculateSellableCap(show)) /
              100
          );
      break;
    }
  }
  if (numberRemaining === 0) {
    return (
      <div>
        <div className="pt-2 pl-4">
          <div className="text-2xl">
            <PiSparkle />
          </div>
        </div>

        <p className="p-4 text-md font-semibold">
          You have met all your ticket bonuses!
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="pt-2 pl-4">
        <div className="text-2xl">
          <PiSparkle />
        </div>
      </div>

      <p className="p-4 text-md font-semibold">
        Your next bonus is projected to trigger in <b>{numberRemaining}</b>{" "}
        tickets
      </p>
    </div>
  );
};

const settledTicketBonusInsight = (show: Show): ReactElement => {
  let metCount = 0;
  let unmetCount = 0;
  const ticketsSoldActual = calculateShowFigures(show).ticketsSoldActual;
  const soldPercent =
    (calculateTicketsSoldActual(show) / calculateSellableCap(show)) * 100;
  show.ticketBonuses.forEach((bonus) => {
    if (!!bonus.threshold && bonus.threshold <= ticketsSoldActual) {
      metCount++;
    } else if (
      !!bonus.thresholdPercent &&
      bonus.thresholdPercent <= soldPercent
    ) {
      metCount++;
    } else {
      unmetCount++;
    }
  });
  if (!unmetCount) {
    return (
      <div>
        <div className="pt-2 pl-4">
          <div className="text-2xl">
            <PiSparkle />
          </div>
        </div>

        <p className="p-4 text-md font-semibold">
          You met all your ticket bonuses!
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="pt-2 pl-4">
        <div className="text-2xl">
          <PiSparkle />
        </div>
      </div>

      <p className="p-4 text-md font-semibold">
        You met {metCount}/{metCount + unmetCount} ticket bonuses
      </p>
    </div>
  );
};

const guaranteeDevianceInsight = (
  show: Show,
  allShows: Show[]
): ReactElement => {
  // Filter shows from the past year
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const pastYearShows = allShows.filter((s) => {
    if (!s.performanceDate) return false;
    const performanceDate = new Date(s.performanceDate);
    return performanceDate >= oneYearAgo;
  });

  // Calculate average guarantee from past year shows (excluding the current show)
  const otherShows = pastYearShows.filter((s) => s.id !== show.id);
  const totalGuarantee = otherShows.reduce(
    (sum, s) => sum + (s.guarantee || 0),
    0
  );
  const averageGuarantee = totalGuarantee / otherShows.length;

  // Calculate the difference between current show and average
  const currentGuarantee = show.guarantee || 0;
  const difference = currentGuarantee - averageGuarantee;
  const percentDifference =
    averageGuarantee > 0 ? (difference / averageGuarantee) * 100 : 0;

  if (difference >= 0) {
    return (
      <div>
        <div className="pt-2 pl-4">
          <div className="text-2xl">
            <PiSparkle />
          </div>
        </div>

        <p className="p-4 text-md font-semibold">
          This guarantee is <b>{formatCurrency(difference)}</b> higher (
          <span className="text-green-500">
            +{formatNumber(percentDifference, 0)}%
          </span>
          ) than expected based on your rolling year average.
        </p>
      </div>
    );
  } else {
    return (
      <div>
        <div className="pt-2 pl-4">
          <div className="text-2xl">
            <PiSparkle />
          </div>
        </div>
        <p className="p-4 text-md font-semibold">
          This guarantee is <b>{formatCurrency(Math.abs(difference))}</b> lower
          (
          <span className="text-red-500">
            {formatNumber(percentDifference, 0)}%
          </span>
          ) than expected based on your rolling year average.
        </p>
      </div>
    );
  }
};

const avgTicketPriceInsight = (show: Show, allShows: Show[]) => {
  // Filter shows from the past year
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  const pastYearShows = allShows.filter((s) => {
    if (!s.performanceDate) return false;
    const performanceDate = new Date(s.performanceDate);
    return performanceDate >= oneYearAgo;
  });

  // If there are no shows in the past year (besides the current one), return a message
  if (pastYearShows.length <= 1) {
    return <p>Not enough historical data to compare ticket prices</p>;
  }

  // Calculate weighted average ticket price for the current show
  const currentShowTickets = show.ticketTypes || [];
  let currentTotalRevenue = 0;
  let currentTotalQuantity = 0;

  currentShowTickets.forEach((ticket) => {
    const price = ticket.price || 0;
    const quantity = ticket.quantity || 0;
    currentTotalRevenue += price * quantity;
    currentTotalQuantity += quantity;
  });

  const currentAvgTicketPrice =
    currentTotalQuantity > 0 ? currentTotalRevenue / currentTotalQuantity : 0;

  // Calculate weighted average ticket price for past year shows (excluding current show)
  const otherShows = pastYearShows.filter((s) => s.id !== show.id);
  let pastYearTotalRevenue = 0;
  let pastYearTotalQuantity = 0;

  otherShows.forEach((s) => {
    const tickets = s.ticketTypes || [];
    tickets.forEach((ticket) => {
      const price = ticket.price || 0;
      const quantity = ticket.quantity || 0;
      pastYearTotalRevenue += price * quantity;
      pastYearTotalQuantity += quantity;
    });
  });

  const pastYearAvgTicketPrice =
    pastYearTotalQuantity > 0
      ? pastYearTotalRevenue / pastYearTotalQuantity
      : 0;

  // Calculate the difference between current show and average
  const difference = currentAvgTicketPrice - pastYearAvgTicketPrice;
  const percentDifference =
    pastYearAvgTicketPrice > 0
      ? (difference / pastYearAvgTicketPrice) * 100
      : 0;

  if (difference >= 0) {
    return (
      <div>
        <div className="pt-2 pl-4">
          <div className="text-2xl">
            <PiSparkle />
          </div>
        </div>

        <div className="p-4 text-md font-semibold">
          Your average ticket price is{" "}
          <b>{formatCurrency(currentAvgTicketPrice)}</b>, which is{" "}
          <b>{formatCurrency(difference)}</b> higher (
          <span className="text-green-500">
            +{formatNumber(percentDifference, 0)}%
          </span>
          ) than expected based on your rolling year average of{" "}
          <b>{formatCurrency(pastYearAvgTicketPrice)}</b>.
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <div className="pt-2 pl-4">
          <div className="text-2xl">
            <PiSparkle />
          </div>
        </div>

        <p className="p-4 text-md font-semibold">
          Your average ticket price is{" "}
          <b>{formatCurrency(currentAvgTicketPrice)}</b>, which is{" "}
          <b>{formatCurrency(Math.abs(difference))}</b> lower (
          <span className="text-red-500">
            {formatNumber(percentDifference, 0)}%
          </span>
          ) than expected based on the Artist's rolling year average of{" "}
          <b>{formatCurrency(pastYearAvgTicketPrice)}</b>.
        </p>
      </div>
    );
  }
};

const versusSplitPointInsight = (show: Show): ReactElement => {
  // Sort tickets by price (cheapest first)
  const sortedTickets = [...show.ticketTypes].sort(
    (a, b) => (a.price || 0) - (b.price || 0)
  );

  // Track how many of each ticket type we've "sold"
  const ticketsSold = sortedTickets.map(() => 0);
  const maxQuantities = sortedTickets.map((ticket) => ticket.quantity || 0);

  let totalTicketsSold = 0;
  let totalTickets = sortedTickets.reduce(
    (acc, ticket) => acc + (ticket?.quantity ?? 0),
    0
  );
  let cheapestIndex = 0;
  let walkout = 0;
  let calculating = true;

  while (cheapestIndex < sortedTickets.length && calculating) {
    // Sell 5 of the cheapest available ticket
    for (
      let i = 0;
      i < 5 && ticketsSold[cheapestIndex] < maxQuantities[cheapestIndex];
      i++
    ) {
      ticketsSold[cheapestIndex]++;
      totalTicketsSold++;

      // Create a simulated show with current ticket sales
      const simulatedTickets = sortedTickets.map((ticket, index) => ({
        ...ticket,
        soldProjected: ticketsSold[index],
      }));

      const simulatedShow = {
        ...show,
        ticketTypes: simulatedTickets,
      };

      const figures = calculateShowFigures(simulatedShow);

      // Check if we've moved beyond the guarantee
      if (
        figures.walkoutProjected > 0 &&
        figures.walkoutProjected >
          (show.guarantee || 0) +
            figures.bonusesProjected +
            figures.showBuyouts +
            calculateNetAdjustments(show) -
            calculateNetDeductions(show)
      ) {
        calculating = false;
        walkout = figures.walkoutProjected;
        break;
      }
    }

    if (!calculating) break;

    // Sell 1 of each other ticket type (if available)
    for (let i = cheapestIndex + 1; i < sortedTickets.length; i++) {
      if (ticketsSold[i] < maxQuantities[i]) {
        ticketsSold[i]++;
        totalTicketsSold++;

        // Create a simulated show with current ticket sales
        const simulatedTickets = sortedTickets.map((ticket, index) => ({
          ...ticket,
          soldProjected: ticketsSold[index],
        }));

        const simulatedShow = {
          ...show,
          ticketTypes: simulatedTickets,
        };

        const figures = calculateShowFigures(simulatedShow);

        // Check if we've moved beyond the guarantee
        if (
          figures.walkoutProjected > 0 &&
          figures.walkoutProjected >
            (show.guarantee || 0) +
              figures.bonusesProjected +
              figures.showBuyouts +
              calculateNetAdjustments(show) -
              calculateNetDeductions(show)
        ) {
          calculating = false;
          walkout = figures.walkoutProjected;
          break;
        }
      }
    }
    // If cheapest ticket type is sold out, move to next cheapest
    if (ticketsSold[cheapestIndex] >= maxQuantities[cheapestIndex]) {
      cheapestIndex++;
    }
  }
  if (sortedTickets.length > 4) {
    return (
      <div>
        <div className="pb-4 pt-2">
          <div className="flex flex-start">
            <div className="ml-4 mt-2">
              <PiSparkle className="text-2xl" />
            </div>
            <p className=" ml-2 pr-4 font-semibold text-sm">
              This deal converts to backend at{" "}
              <b>
                {formatNumber(totalTicketsSold)} (
                {formatPercentage((totalTicketsSold / totalTickets) * 100)})
              </b>{" "}
              tickets sold
            </p>
          </div>
          <table className="ml-4 w-full text-xs">
            <tbody>
              {ticketsSold.slice(0, 5).map((sold, index) => (
                <tr key={index}>
                  <td className="w-6">
                    <FaTicketAlt className="w-3 h-3" />
                  </td>
                  <td className="w-32 max-w-32 overflow-x-hidden font-bold font-xs truncate">
                    <Tooltip
                      content={sortedTickets[index].name}
                      display={sortedTickets[index].name?.length > 14}
                    >
                      <>{stringLengthLimiter(sortedTickets[index].name, 14)}</>
                    </Tooltip>
                  </td>
                  <td className="">{formatNumber(sold)} tickets</td>
                </tr>
              ))}
              {ticketsSold[5] > 0 && (
                <tr>
                  <td className="w-6">
                    <FaTicketAlt className="w-3 h-3" />
                  </td>
                  <td className="" colSpan={2}>
                    And more...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="font-semibold text-sm ml-4">
            At the split point, the walkout is {formatCurrency(walkout)}
          </p>
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <div className="pt-2 pl-4">
          <div className="text-2xl">
            <PiSparkle />
          </div>
        </div>
        <div className="p-4">
          <p className="font-semibold text-sm">
            This deal converts to backend at{" "}
            <b>
              {formatNumber(totalTicketsSold)} (
              {formatPercentage((totalTicketsSold / totalTickets) * 100)})
            </b>{" "}
            tickets sold
          </p>
          <table className="w-full text-xs">
            <tbody>
              {ticketsSold.slice(0, 5).map((sold, index) => (
                <tr key={index}>
                  <td className="w-6">
                    <FaTicketAlt className="w-3 h-3" />
                  </td>
                  <td className="w-32 max-w-32 overflow-x-hidden font-bold truncate">
                    <Tooltip
                      content={sortedTickets[index].name}
                      display={sortedTickets[index].name?.length > 14}
                    >
                      <>{stringLengthLimiter(sortedTickets[index].name, 14)}</>
                    </Tooltip>
                  </td>
                  <td className="">{formatNumber(sold)} tickets</td>
                </tr>
              ))}
              {ticketsSold[5] > 0 && (
                <tr>
                  <td className="w-6">
                    <FaTicketAlt className="w-3 h-3" />
                  </td>
                  <td className="" colSpan={2}>
                    And more...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="font-semibold text-sm mt-1">
            At the split point, the walkout is {formatCurrency(walkout)}
          </p>
        </div>
      </div>
    );
  }
};

const simulateTicketSaleProjections = (
  show: Show,
  percentSold: number
): TicketType[] => {
  const showClone = structuredClone(show);
  const tickets = showClone.ticketTypes || [];
  tickets.forEach((ticket) => {
    ticket.soldProjected = Math.floor((ticket.quantity ?? 0) * percentSold);
  });
  return tickets;
};

const revShareQuartileSoldInsight = (show: Show) => {
  const quarterSoldFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 0.25),
  });
  const halfSoldFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 0.5),
  });
  const threeQuarterSoldFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 0.75),
  });
  const selloutFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 1),
  });

  return (
    <div>
      <div className="pt-2 pl-4">
        <div className="text-2xl">
          <PiSparkle />
        </div>
      </div>
      <div className="p-4">
        <p className="font-semibold text-sm">
          Here is a breakdown of the Artist's share of revenue at box office
          milestones:
        </p>
        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>25%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-1/4 h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>{" "}
          <b className="text-sm">
            {formatCurrency(quarterSoldFigures.artistShareOfRevenueProjected)}
          </b>
        </div>
        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>50%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-1/2 h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>{" "}
          <b className="text-sm">
            {formatCurrency(halfSoldFigures.artistShareOfRevenueProjected)}
          </b>
        </div>
        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>75%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-3/4 h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>{" "}
          <b className="text-sm">
            {formatCurrency(
              threeQuarterSoldFigures.artistShareOfRevenueProjected
            )}
          </b>
        </div>
        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>100%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-full h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>{" "}
          <b className="text-sm">
            {formatCurrency(selloutFigures.artistShareOfRevenueProjected)}
          </b>
        </div>
      </div>
    </div>
  );
};

const VersusQuartileSoldInsight = (show: Show) => {
  const quarterSoldFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 0.25),
  });
  const halfSoldFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 0.5),
  });
  const threeQuarterSoldFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 0.75),
  });
  const selloutFigures = calculateShowFigures({
    ...show,
    ticketTypes: simulateTicketSaleProjections(show, 1),
  });

  return (
    <div>
      <div className="pt-2 pl-4">
        <div className="text-2xl">
          <PiSparkle />
        </div>
      </div>
      <div className="p-4">
        <p className="font-semibold text-sm">
          Here is a breakdown of the Artist's share of revenue at box office
          milestones:
        </p>
        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>25%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-1/4 h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>
          <b className="text-sm">
            {quarterSoldFigures.artistShareOfRevenueProjected === 0
              ? `${formatCurrency(show.guarantee || 0)} (Guarantee)`
              : formatCurrency(
                  quarterSoldFigures.artistShareOfRevenueProjected
                )}
          </b>{" "}
        </div>
        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>50%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-1/2 h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>
          <b className="text-sm">
            {halfSoldFigures.artistShareOfRevenueProjected === 0
              ? `${formatCurrency(show.guarantee || 0)} (Guarantee)`
              : formatCurrency(halfSoldFigures.artistShareOfRevenueProjected)}
          </b>{" "}
        </div>
        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>75%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-3/4 h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>
          <b className="text-sm">
            {threeQuarterSoldFigures.artistShareOfRevenueProjected === 0
              ? `${formatCurrency(show.guarantee || 0)} (Guarantee)`
              : formatCurrency(
                  threeQuarterSoldFigures.artistShareOfRevenueProjected
                )}
          </b>{" "}
        </div>

        <div className=" flex items-center gap-2 ">
          <p className="font-semibold text-xs">
            <div>100%</div>
            <div className="w-full h-1 bg-gray-200 rounded">
              <div className="w-full h-1 bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal to-custom-dark-purple via-custom-purple rounded"></div>
            </div>
          </p>
          <b className="text-sm">
            {selloutFigures.artistShareOfRevenueProjected === 0
              ? `${formatCurrency(show.guarantee || 0)} (Guarantee)`
              : formatCurrency(selloutFigures.artistShareOfRevenueProjected)}
          </b>{" "}
        </div>
      </div>
    </div>
  );
};

const BoxOfficeProjectedInsight = (show: Show): ReactElement => {
  const showFigures = calculateShowFigures(show);

  return (
    <div>
      <div className="pt-2 pl-4">
        <div className="text-2xl">
          <PiSparkle />
        </div>
      </div>
      <div className="p-4">
        <p className="font-semibold text-sm">
          This show has potential net box office receipts (NBOR) of{" "}
          <b>{formatCurrency(showFigures.ticketRevenuePotential)}.</b>
        </p>
        <p className="font-semibold text-sm">
          The Artist's current projected ticket sales would result in NBOR of{" "}
          <b>{formatCurrency(showFigures.ticketRevenueProjected)}</b>.
        </p>
      </div>
    </div>
  );
};

const BoxOfficeActualInsight = (show: Show): ReactElement => {
  const showFigures = calculateShowFigures(show);

  return (
    <div>
      <div className="pt-2 pl-4">
        <div className="text-2xl">
          <PiSparkle />
        </div>
      </div>
      <div className="p-4">
        <p className="font-semibold">Net Box Office:</p>
        <p className="font-semibold">
          Actual: <b>{formatCurrency(showFigures.ticketRevenueActual)}</b>
        </p>
        <p className="font-semibold">
          Potential: <b>{formatCurrency(showFigures.ticketRevenuePotential)}</b>
        </p>
      </div>
    </div>
  );
};

const flatFeeInsights = (show: Show, allShows: Show[]): ReactElement[] => {
  const insights: ReactElement[] = [];
  if (show.ticketBonuses?.length > 0) {
    insights.push(
      isShowInActualStatus(show)
        ? settledTicketBonusInsight(show)
        : projectedTicketBonusInsight(show)
    );
  }
  if (allShows?.length > 1) {
    insights.push(guaranteeDevianceInsight(show, allShows));
    insights.push(avgTicketPriceInsight(show, allShows));
  }
  return insights;
};

const revShareInsights = (show: Show, allShows: Show[]): ReactElement[] => {
  const insights: ReactElement[] = [];
  if (show.ticketTypes?.length > 0) {
    if (!isShowInActualStatus(show))
      insights.push(revShareQuartileSoldInsight(show));
    isShowInActualStatus(show)
      ? insights.push(BoxOfficeActualInsight(show))
      : insights.push(BoxOfficeProjectedInsight(show));
  }
  if (allShows?.length > 1) {
    insights.push(guaranteeDevianceInsight(show, allShows));
    insights.push(avgTicketPriceInsight(show, allShows));
  }
  if (show.ticketBonuses?.length > 0) {
    insights.push(
      isShowInActualStatus(show)
        ? settledTicketBonusInsight(show)
        : projectedTicketBonusInsight(show)
    );
  }

  return insights;
};

const versusInsights = (show: Show, allShows: Show[]): ReactElement[] => {
  const insights: ReactElement[] = [];
  if (show.ticketTypes?.length > 0) {
    if (!isShowInActualStatus(show)) {
      insights.push(versusSplitPointInsight(show));
      insights.push(VersusQuartileSoldInsight(show));
    }
    isShowInActualStatus(show)
      ? insights.push(BoxOfficeActualInsight(show))
      : insights.push(BoxOfficeProjectedInsight(show));
  }
  if (allShows?.length > 1) {
    insights.push(guaranteeDevianceInsight(show, allShows));
    insights.push(avgTicketPriceInsight(show, allShows));
  }
  if (show.ticketBonuses?.length > 0) {
    insights.push(
      isShowInActualStatus(show)
        ? settledTicketBonusInsight(show)
        : projectedTicketBonusInsight(show)
    );
  }

  return insights;
};

export const showInsights = (show: Show, allShows: Show[]): ReactElement[] => {
  switch (show.dealType) {
    case ShowDealType.FLAT_FEE:
      return flatFeeInsights(show, allShows);
    case ShowDealType.REV_SPLIT:
      return revShareInsights(show, allShows);
    case ShowDealType.VERSUS:
      return versusInsights(show, allShows);
    default:
      return [];
  }
};
